import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateIntervalFields } from '@mews-commander/form';
import { DateTimeService, DateTimeUtils, lt, useAppClockService, useTimezone } from '@mews/framework';
import { IconButton, IconName, Stack, StackItem } from '@optimus-web/core';
import { FieldSectionName, PolicyExceptionsDatesFieldName } from '../../../types';
import { PolicyExceptionFormValues } from '../../../../../schemas';

interface Props {
    disabled: boolean;
    index: number;
    onRemove: () => void;
}
export const PolicyExceptionsDateIntervalRow = ({ disabled, index, onRemove }: Props) => {
    const {
        formState: {
            errors: { dates },
        },
        trigger,
        watch,
    } = useFormContext<PolicyExceptionFormValues>();

    const timezone = useTimezone();
    const appClockService = useAppClockService();
    const minDate = useMemo(() => {
        const currentDateTime = DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone);
        return DateTimeUtils.dateTimeToStructuredDate(currentDateTime);
    }, [timezone, appClockService]);

    const startDateName =
        `${FieldSectionName.Dates}.${PolicyExceptionsDatesFieldName.TimeUnitIntervals}.${index}.${PolicyExceptionsDatesFieldName.FirstTimeUnitStartUtc}` as const;
    const endDateName =
        `${FieldSectionName.Dates}.${PolicyExceptionsDatesFieldName.TimeUnitIntervals}.${index}.${PolicyExceptionsDatesFieldName.LastTimeUnitStartUtc}` as const;

    const startDate = watch(startDateName);
    const endDate = watch(endDateName);

    return (
        <Stack itemAlignment={dates?.timeUnitIntervals?.[index] ? 'center' : 'end'} spacing="100">
            <StackItem grow>
                <DateIntervalFields
                    inline
                    minDate={minDate}
                    mode="utc"
                    startDateProps={{
                        name: startDateName,
                        onChange: async (e, startDateValue) => {
                            if (startDateValue && endDate) {
                                await trigger(endDateName);
                            }
                        },
                        label: lt('PolicyExceptionFormDatesStarts'),
                        required: true,
                    }}
                    endDateProps={{
                        name: endDateName,
                        onChange: async (e, endDateValue) => {
                            if (startDate && endDateValue) {
                                await trigger(startDateName);
                            }
                        },
                        label: lt('PolicyExceptionFormDatesEnds'),
                        required: true,
                    }}
                />
            </StackItem>
            <IconButton disabled={disabled} icon={IconName.Delete} onClick={onRemove} type="button" />
        </Stack>
    );
};
